import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { usePersistedState } from '../hooks/usePersistedState';
import { translations as initialTranslations } from '../translations';
import type { Translations, ClientLogo, SiteLanguage } from '../types';
import { DEFAULT_SITE_LANGUAGES } from '../types/translations';

interface ContentContextType {
  content: Translations;
  setContent: (value: Translations | ((prev: Translations) => Translations)) => void;
  logos: ClientLogo[];
  setLogos: (value: ClientLogo[] | ((prev: ClientLogo[]) => ClientLogo[])) => void;
  siteLanguages: SiteLanguage[];
  refreshLanguages: () => Promise<void>;
}

const ContentContext = createContext<ContentContextType | null>(null);

interface ContentProviderProps {
  children: ReactNode;
}

export function ContentProvider({ children }: ContentProviderProps) {
  // Key is versioned: bump the suffix whenever the translations *shape* changes
  // (e.g. services repositioning renamed `services.shopify` → `services.aiAgents`;
  // v3 added `hero.systems` for the system-screenshot carousel).
  // This discards stale cached content that would be missing newly-added keys.
  const [content, setContent] = usePersistedState<Translations>(
    'rp_content_v3',
    initialTranslations as Translations
  );

  const [logos, setLogos] = usePersistedState<ClientLogo[]>('rp_logos', []);

  // Language list — initialised from static defaults so the site renders correctly
  // before the API responds. Updated in the background once.
  const [siteLanguages, setSiteLanguages] = useState<SiteLanguage[]>(DEFAULT_SITE_LANGUAGES);

  const refreshLanguages = async () => {
    try {
      const r = await fetch('/api/languages');
      if (r.ok) {
        const data = await r.json() as unknown;
        if (Array.isArray(data) && data.length > 0) {
          setSiteLanguages(data as SiteLanguage[]);
        }
      }
    } catch {
      // Keep static defaults on failure
    }
  };

  useEffect(() => { void refreshLanguages(); }, []);

  const value = useMemo(
    () => ({ content, setContent, logos, setLogos, siteLanguages, refreshLanguages }),
    [content, setContent, logos, setLogos, siteLanguages]
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useContent(): ContentContextType {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
}
